import React, { useState, useEffect, useContext } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import OrderService from '../../service/order.service';
import { formatCentsToBRL, formatVariants } from '../../utils/currency';
import { getPaymentLabel, getMpMethodLabel, isOnlinePayment, isOrderPaid } from '../../utils/orderPayment';
import { getOrderSourceLabel } from '../../utils/orderSource';
import { useNotification } from '../../contexts/NotificationContext';
import { ApiContext } from '../../contexts/ApiContext';
import { accountPath } from '../../utils/accountPath';
import PrintService from '../../service/print.service';
import { formatPhone } from '../../utils/phone';

const EDIT_WINDOW_MS = 48 * 60 * 60 * 1000;

const ACTIVITY_LABELS = {
  STATUS: 'alterou o status',
  EDIT: 'editou o pedido',
  RESOLVE: 'resolveu o pedido',
  CREATE: 'criou o pedido',
};

const DEFAULT_STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pendente' },
  { value: 'CONFIRMED', label: 'Confirmado' },
  { value: 'PREPARING', label: 'Preparando' },
  { value: 'READY_FOR_DELIVERY', label: 'Pronto para entrega' },
  { value: 'OUT_FOR_DELIVERY', label: 'Saiu para entrega' },
  { value: 'DELIVERED', label: 'Entregue' },
  { value: 'CANCELLED', label: 'Cancelado' },
];

const ORDER_TYPE_LABELS = {
  DELIVERY: 'Entrega',
  PICKUP: 'Retirada',
  DINE_IN: 'Mesa',
};

const OrderDetailsModal = ({ isOpen, toggle, order, statuses, onUpdated }) => {
  const navigate = useNavigate();
  const { account } = useContext(ApiContext);
  const [status, setStatus] = useState(order?.status || 'PENDING');
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [activities, setActivities] = useState([]);
  const [fullOrder, setFullOrder] = useState(null);
  const { notify } = useNotification();

  const displayOrder = fullOrder || order;

  const canEdit = order ? (Date.now() - new Date(order.createdAt).getTime()) < EDIT_WINDOW_MS : false;

  useEffect(() => {
    setStatus(order?.status || 'PENDING');
  }, [order?.id, order?.status]);

  useEffect(() => {
    if (isOpen && order?.id) {
      OrderService.getOrderById(order.id)
        .then((full) => {
          setFullOrder(full);
          setActivities(full?.activities || []);
        })
        .catch(() => {
          setFullOrder(null);
          setActivities([]);
        });
    } else {
      setFullOrder(null);
    }
  }, [isOpen, order?.id]);

  const statusOptions = (statuses && statuses.length)
    ? statuses.filter((s) => s.active).map((s) => ({ value: s.status, label: s.label }))
    : DEFAULT_STATUS_OPTIONS;

  const handleSave = async () => {
    if (!order) return;
    setSaving(true);
    try {
      await OrderService.updateOrder(order.id, { status });
      notify('tr', 'success', 'Status atualizado', 'icon-check-2');
      if (onUpdated) onUpdated(order.id, status);
      toggle();
    } catch (err) {
      console.error(err);
      notify('tr', 'danger', 'Erro ao atualizar pedido', 'icon-alert-circle-exc');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = async (target = 'KITCHEN') => {
    if (!order?.id || !account?.id) return;
    setPrinting(true);
    try {
      await PrintService.createJob(account.id, { orderId: order.id, target });
      notify('tr', 'success', 'Enviado para impressão', 'icon-check-2');
    } catch (err) {
      const msg = err?.response?.data?.message;
      notify(
        'tr',
        'danger',
        Array.isArray(msg) ? msg.join(', ') : msg || 'Erro ao imprimir',
        'icon-alert-circle-exc',
      );
    } finally {
      setPrinting(false);
    }
  };

  const handleDownloadPdf = () => {
    const o = displayOrder || order;
    const esc = (v) => String(v ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const money = formatCentsToBRL;
    const created = new Date(o.createdAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const statusLabel = statusOptions.find((s) => s.value === o.status)?.label || o.status;
    const typeLabel = `${ORDER_TYPE_LABELS[o.type] || o.type || 'Entrega'}${o.type === 'DINE_IN' && (o.tableName || o.table?.name) ? ` · ${o.tableName || o.table?.name}` : ''}`;
    const addr = o.type !== 'DINE_IN' && o.address ? fullAddress(o.address) : '';
    const rows = o.items.map((it) => `
      <tr>
        <td class="qty">${it.quantity}x</td>
        <td>
          <div class="item">${esc(it.name)}${it.sizeName ? ` · ${esc(it.sizeName)}` : ''}</div>
          ${it.variants?.length ? `<div class="sub">+ ${esc(formatVariants(it.variants, true))}</div>` : ''}
          ${it.note ? `<div class="sub">Obs: ${esc(it.note)}</div>` : ''}
        </td>
        <td class="num">${money(it.unitPriceCents)}</td>
        <td class="num">${money(it.lineTotalCents)}</td>
      </tr>`).join('');

    const html = `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
      <title>Pedido #${o.id} - ${esc(account?.name || '')}</title>
      <style>
        @page { size: A4; margin: 16mm; }
        * { box-sizing: border-box; }
        body { font-family: -apple-system, 'Segoe UI', Roboto, Arial, sans-serif; color: #1f2937; font-size: 12px; margin: 0; }
        header { display: flex; justify-content: space-between; align-items: center; padding-bottom: 14px; border-bottom: 2px solid #111827; }
        .brand { display: flex; align-items: center; gap: 12px; }
        .brand img { width: 52px; height: 52px; border-radius: 8px; object-fit: cover; }
        .brand h1 { font-size: 18px; margin: 0; }
        .muted { color: #6b7280; }
        .doc { text-align: right; }
        .doc .n { font-size: 20px; font-weight: 800; }
        .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin: 18px 0; }
        .box { border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 12px; }
        .box h3 { font-size: 10px; text-transform: uppercase; letter-spacing: .06em; color: #6b7280; margin: 0 0 6px; }
        .box p { margin: 2px 0; white-space: pre-line; }
        table { width: 100%; border-collapse: collapse; }
        th { text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: .06em; color: #6b7280; border-bottom: 1px solid #d1d5db; padding: 6px 4px; }
        td { padding: 8px 4px; border-bottom: 1px solid #f3f4f6; vertical-align: top; }
        .qty { width: 40px; font-weight: 700; }
        .num { text-align: right; white-space: nowrap; width: 90px; }
        th.num { text-align: right; }
        .item { font-weight: 600; }
        .sub { color: #6b7280; font-size: 11px; margin-top: 2px; }
        .totals { margin-left: auto; width: 260px; margin-top: 12px; }
        .totals div { display: flex; justify-content: space-between; padding: 3px 0; }
        .totals .grand { border-top: 2px solid #111827; margin-top: 6px; padding-top: 8px; font-size: 15px; font-weight: 800; }
        .note { margin-top: 16px; }
        footer { margin-top: 28px; text-align: center; font-size: 10px; color: #9ca3af; }
      </style></head><body>
      <header>
        <div class="brand">
          ${account?.logoUrl ? `<img src="${esc(account.logoUrl)}" alt="">` : ''}
          <div><h1>${esc(account?.name || '')}</h1>${account?.cnpj ? `<div class="muted">CNPJ ${esc(account.cnpj)}</div>` : ''}</div>
        </div>
        <div class="doc"><div class="n">Pedido #${o.id}</div><div class="muted">${created}</div></div>
      </header>
      <div class="grid">
        <div class="box"><h3>Cliente</h3><p><strong>${esc(o.customerName)}</strong></p><p>${esc(formatPhone(o.customerPhone) || o.customerPhone)}</p>${addr ? `<p>${esc(addr)}</p>` : ''}</div>
        <div class="box"><h3>Pedido</h3><p>Tipo: ${esc(typeLabel)}</p><p>Pagamento: ${esc(getPaymentLabel(o.payment))}${isOnlinePayment(o) ? (isOrderPaid(o) ? ' · Pago' : ' · Aguardando pagamento') : ''}</p><p>Status: ${esc(statusLabel)}</p><p>Origem: ${esc(getOrderSourceLabel(o))}</p></div>
      </div>
      <table>
        <thead><tr><th>Qtd</th><th>Item</th><th class="num">Unitário</th><th class="num">Total</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="totals">
        <div><span>Subtotal</span><span>${money(o.subtotalCents)}</span></div>
        ${o.discountCents > 0 ? `<div><span>Desconto${o.couponCode ? ` (${esc(o.couponCode)})` : ''}</span><span>− ${money(o.discountCents)}</span></div>` : ''}
        <div><span>Taxa de entrega</span><span>${money(o.deliveryCents || 0)}</span></div>
        <div class="grand"><span>Total</span><span>${money(o.totalCents)}</span></div>
      </div>
      ${o.note?.trim() ? `<div class="box note"><h3>Observação</h3><p>${esc(o.note)}</p></div>` : ''}
      <footer>Documento gerado em ${new Date().toLocaleString('pt-BR')} · Sem valor fiscal</footer>
      </body></html>`;

    const frame = document.createElement('iframe');
    frame.style.cssText = 'position:fixed;width:0;height:0;border:0;right:0;bottom:0';
    document.body.appendChild(frame);
    const doc = frame.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
    const print = () => {
      frame.contentWindow.focus();
      frame.contentWindow.print();
      setTimeout(() => frame.remove(), 1000);
    };
    const logo = doc.querySelector('img');
    if (logo && !logo.complete) {
      logo.onload = print;
      logo.onerror = print;
    } else {
      print();
    }
  };

  if (!order) return null;

  const orderData = displayOrder || order;

  const fullAddress = (addr) => {
    if (!addr) return null;
    const parts = [];
    const street = addr.street || addr.logradouro || addr.addressLine || '';
    const number = addr.number || addr.numero || '';
    const complement = addr.complement || addr.complemento || '';
    const district = addr.district || addr.bairro || addr.neighborhood || '';
    const city = addr.city || addr.cidade || '';
    const state = addr.state || addr.uf || '';
    const zipcode = addr.zipcode || addr.cep || addr.postalCode || '';
    const reference = addr.reference || addr.referencia || '';

    if (street) parts.push(`${street}${number ? `, ${number}` : ''}`);
    if (complement) parts.push(complement);
    if (district) parts.push(district);
    if (city || state) parts.push(`${city}${city && state ? '/' : ''}${state}`);
    if (zipcode) parts.push(zipcode);
    if (reference) parts.push(`Ref: ${reference}`);

    return parts.filter(Boolean).join('\n');
  };

  return (
    <Modal 
      isOpen={isOpen} 
      toggle={toggle} 
      size="lg"
      style={{ marginTop: '-162px' }}
      modalClassName="modal-top"
    >
      <ModalHeader toggle={toggle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div style={{ fontSize: 20, fontWeight: 800 }}>Pedido #{orderData.id} — {orderData.customerName}</div>
        </div>
      </ModalHeader>
      <ModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
        <Row>
          <Col md={8}>
            <h3 style={{ marginBottom: 12, fontSize: 20 }}>Itens do Pedido</h3>
            {orderData.items.map((it) => (
              <div key={it.id} style={{ 
                borderBottom: '1px solid #e9ecef', 
                padding: '12px 0',
                marginBottom: 8 
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>
                      <strong>{it.quantity}x</strong> {it.name}{it.sizeName ? ` · ${it.sizeName}` : ''}
                    </div>
                    {it.description && (
                      <div style={{ fontSize: 12, color: '#6c757d', marginTop: 2 }}>
                        {it.description}
                      </div>
                    )}
                  </div>
                  <div style={{ fontWeight: 600, marginLeft: 12 }}>
                    {formatCentsToBRL(it.lineTotalCents)}
                  </div>
                </div>
                
                {it.variants && it.variants.length > 0 && (
                  <div style={{ 
                    fontSize: 13, 
                    color: '#525f7f', 
                    marginTop: 4,
                    paddingLeft: 8,
                    borderLeft: '2px solid #dee2e6'
                  }}>
                    <strong>Acréscimos:</strong> {formatVariants(it.variants, true)}
                  </div>
                )}
                
                {it.note && (
                  <div style={{ 
                    marginTop: 6, 
                    fontSize: 13, 
                    color: '#ffc107',
                    fontStyle: 'italic',
                    paddingLeft: 8,
                    borderLeft: '2px solid #ffc107'
                  }}>
                    <strong>Obs:</strong> {it.note}
                  </div>
                )}
                
                <div style={{ 
                  fontSize: 12, 
                  color: '#777', 
                  marginTop: 6,
                  display: 'flex',
                  gap: 12
                }}>
                  <span>Preço unitário: {formatCentsToBRL(it.unitPriceCents)}</span>
                  {it.variants && it.variants.length > 0 && (
                    <span>
                      + Extras: {formatCentsToBRL(it.variants.reduce((sum, v) => sum + (v.extraCents || 0), 0))}
                    </span>
                  )}
                </div>
              </div>
            ))}

            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '2px solid #dee2e6' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 6, fontSize: 14 }}>
                <div>Subtotal:</div>
                <div>{formatCentsToBRL(orderData.subtotalCents)}</div>
              </div>
              {orderData.discountCents > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 6, fontSize: 14, color: '#00c853' }}>
                  <div>Desconto{orderData.couponCode ? ` (${orderData.couponCode})` : ''}:</div>
                  <div>− {formatCentsToBRL(orderData.discountCents)}</div>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 6, fontSize: 14 }}>
                <div>Taxa de Entrega:</div>
                <div>{formatCentsToBRL(orderData.deliveryCents || 0)}</div>
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                paddingTop: 12, 
                marginTop: 8,
                borderTop: '1px solid #dee2e6',
                fontSize: 18,
                fontWeight: 700,
                color: '#1d8cf8'
              }}>
                <div>Total:</div>
                <div>{formatCentsToBRL(orderData.totalCents)}</div>
              </div>
            </div>
          </Col>
          <Col md={4}>
            <h3 style={{ marginBottom: 12, fontSize: 20 }}>Informações do Cliente</h3>
            <div style={{ marginBottom: 8 }}>
              <strong style={{ fontSize: 16 }}>{orderData.customerName}</strong>
            </div>
            <div style={{ fontSize: 14, color: '#525f7f', marginBottom: 4 }}>
              <i className="tim-icons icon-mobile" /> {orderData.customerPhone}
            </div>

            {orderData.note?.trim() && (
              <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e9ecef' }}>
                <h5 style={{ marginBottom: 8, fontSize: 15, fontWeight: 600 }}>
                  <i className="tim-icons icon-notes" /> Observação
                </h5>
                <div style={{ fontSize: 14, color: '#344675', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                  {orderData.note}
                </div>
              </div>
            )}
            
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e9ecef' }}>
              <h5 style={{ marginBottom: 8, fontSize: 15, fontWeight: 600 }}>
                <i className="tim-icons icon-delivery-fast" /> Tipo do Pedido
              </h5>
              <div style={{ fontSize: 14, color: '#525f7f' }}>
                {ORDER_TYPE_LABELS[orderData.type] || orderData.type || 'Entrega'}
                {orderData.type === 'DINE_IN' && (orderData.tableName || orderData.table?.name)
                  ? ` · ${orderData.tableName || orderData.table?.name}`
                  : ''}
              </div>
            </div>

            {orderData.type !== 'DINE_IN' && (
              <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e9ecef' }}>
                <h4 style={{ marginBottom: 8, fontSize: 16, fontWeight: 600 }}>
                  <i className="tim-icons icon-square-pin" /> Endereço de Entrega
                </h4>
                {orderData.address ? (
                  <div style={{ fontSize: 14, whiteSpace: 'pre-line', lineHeight: 1.6, color: '#344675' }}>
                    {fullAddress(orderData.address)}
                  </div>
                ) : (
                  <div style={{ fontSize: 14, color: '#777', fontStyle: 'italic' }}>
                    Endereço não informado
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e9ecef' }}>
              <h5 style={{ marginBottom: 8, fontSize: 15, fontWeight: 600 }}>
                <i className="tim-icons icon-credit-card" /> Forma de Pagamento
              </h5>
              <div style={{ fontSize: 14, color: '#525f7f' }}>
                {getPaymentLabel(orderData.payment)}
              </div>
              {isOnlinePayment(orderData) && (
                <div style={{ marginTop: 8 }}>
                  <span style={{
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '3px 8px',
                    borderRadius: 4,
                    background: isOrderPaid(orderData) ? 'rgba(0, 187, 51, 0.2)' : 'rgba(255, 193, 7, 0.2)',
                    color: isOrderPaid(orderData) ? '#4ade80' : '#ffc107',
                  }}>
                    {isOrderPaid(orderData) ? 'PAGO' : 'AGUARDANDO PAGAMENTO'}
                  </span>
                </div>
              )}
              {orderData.paidAt && (
                <div style={{ fontSize: 13, color: '#4ade80', marginTop: 6 }}>
                  Pago em {new Date(orderData.paidAt).toLocaleString('pt-BR')}
                </div>
              )}
              {isOnlinePayment(orderData) && isOrderPaid(orderData) && (
                <div style={{ marginTop: 10, fontSize: 13, color: '#344675' }}>
                  {orderData.mpPaymentMethod && (
                    <div style={{ marginBottom: 4 }}>
                      <strong>Método MP:</strong> {getMpMethodLabel(orderData.mpPaymentMethod)}
                    </div>
                  )}
                  {orderData.mercadoPagoPaymentId && (
                    <div style={{ marginBottom: 4, fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                      <strong>ID transação:</strong> {orderData.mercadoPagoPaymentId}
                    </div>
                  )}
                  {orderData.mpPayerName && (
                    <div style={{ marginBottom: 4 }}>
                      <strong>Pagador:</strong> {orderData.mpPayerName}
                    </div>
                  )}
                  {orderData.mpPayerEmail && (
                    <div>
                      <strong>E-mail:</strong> {orderData.mpPayerEmail}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e9ecef' }}>
              <h5 style={{ marginBottom: 8, fontSize: 15, fontWeight: 600 }}>
                <i className="tim-icons icon-settings" /> Status do Pedido
              </h5>
              {canEdit ? (
                <select
                  className="form-control"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  style={{ fontSize: 14 }}
                >
                  {statusOptions.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              ) : (
                <div style={{ fontSize: 14, color: '#525f7f' }}>
                  {statusOptions.find((s) => s.value === orderData.status)?.label || orderData.status}
                </div>
              )}
            </div>

            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e9ecef' }}>
              <h5 style={{ marginBottom: 8, fontSize: 15, fontWeight: 600 }}>
                <i className="tim-icons icon-time-alarm" /> Informações Adicionais
              </h5>
              <div style={{ fontSize: 13, color: '#6c757d' }}>
                <div style={{ marginBottom: 4 }}>
                  <strong>Pedido #:</strong> {orderData.id}
                </div>
                <div style={{ marginBottom: 4 }}>
                  <strong>Origem:</strong> {getOrderSourceLabel(orderData)}
                </div>
                <div style={{ marginBottom: 4 }}>
                  <strong>Criado em:</strong><br/>
                  {new Date(orderData.createdAt).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
                {orderData.updatedAt && orderData.updatedAt !== orderData.createdAt && (
                  <div style={{ marginBottom: 4 }}>
                    <strong>Atualizado em:</strong><br/>
                    {new Date(orderData.updatedAt).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                )}
              </div>
            </div>

            {activities.length > 0 && (
              <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e9ecef' }}>
                <h5 style={{ marginBottom: 8, fontSize: 15, fontWeight: 600 }}>
                  <i className="tim-icons icon-notes" /> Atividade
                </h5>
                <div style={{ fontSize: 12, color: '#6c757d' }}>
                  {activities.map((a) => (
                    <div key={a.id} style={{ marginBottom: 6, lineHeight: 1.4 }}>
                      <strong style={{ color: '#344675' }}>{a.user?.name || 'Sistema'}</strong>{' '}
                      {ACTIVITY_LABELS[a.action] || a.action}
                      {a.detail ? ` · ${a.detail}` : ''}
                      <div style={{ color: '#666' }}>
                        {new Date(a.createdAt).toLocaleString('pt-BR', {
                          day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter className="pt-3">
        <div className="w-100 d-flex justify-content-between">
          {canEdit ? (
            <Button
              color="info"
              onClick={() => { toggle(); navigate(accountPath(account?.id, `/pedidos/editar/${order.id}`)); }}
            >
              <i className="tim-icons icon-pencil" /> Editar Pedido
            </Button>
          ) : (
            <span />
          )}
          <div>
            <Button
              color="default"
              outline
              className="mr-2"
              onClick={handleDownloadPdf}
              title="Abre a janela de impressão; escolha “Salvar como PDF”"
            >
              <i className="tim-icons icon-cloud-download-93" /> Baixar PDF
            </Button>
            {account?.printingEnabled && (
              <Button
                color="warning"
                outline
                className="mr-2"
                onClick={() => handlePrint('KITCHEN')}
                disabled={printing}
              >
                <i className="tim-icons icon-paper" /> {printing ? 'Imprimindo...' : 'Imprimir'}
              </Button>
            )}
            <Button color="secondary" onClick={toggle} className="mr-2">Fechar</Button>
            {canEdit && (
              <Button color="primary" onClick={handleSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
            )}
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default OrderDetailsModal;
